import { Container } from 'pixi.js';
import { RaceEventCarLayout } from '../../../../../layouts/raceEvent/RaceEventCarLayout';
import {
  createLayoutAnimationPlayer,
  playLayoutAnimation,
} from '../../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { applyLayoutState } from '../../../../libs/layouts/populateLayouts/applyLayoutState';
import type { PlatformUser } from '../../../../app/platform/Platform';
import { linear } from '../../../../libs/TweenManager/interpolations';
import { triggerParticleFX } from '../../../../libs/particles/triggerParticleFX';
import type { RaceTrackProgress } from '../RaceEventPopupTrackView';

interface RaceEventCarViewConfig {
  initProgress: RaceTrackProgress;
  player: PlatformUser;
  positions: {
    preStartPosition: Container;
    startPosition: Container;
    preFinishedPosition: Container;
    finishedPosition: Container;
  };
  setupForRaceStartAnimation: boolean;
}

export class RaceEventCarView extends Container {
  private readonly layout = new RaceEventCarLayout();

  private readonly bounceAnimator = createLayoutAnimationPlayer(this.layout, 'Car_Bounce');

  private readonly scaleAnimator = createLayoutAnimationPlayer(this.layout, 'Car_Scale');

  private currentRelY = 0;

  constructor(private readonly config: RaceEventCarViewConfig) {
    super();
    this.init();

    this.pixi.ticker.add(this.updateCarY);
    this.once('destroyed', () => {
      this.pixi.ticker.remove(this.updateCarY);
    });
  }

  public async animatePreStart(): Promise<void> {
    await this.tweenTo({ currentRelY: 0 }, 500, 'sinOut', true).asyncOnComplete();
    this.setWheelAnimation(false);
  }

  public animateRaceStart(): void {
    this.setWheelAnimation(true);
    this.bounceAnimator.play(true);
  }

  private init(): void {
    const { setupForRaceStartAnimation, initProgress, player } = this.config;

    this.layout.root.props({
      parent: this,
    });

    if (player.currentUser) {
      applyLayoutState(this.layout, 'player');
    }

    this.currentRelY = setupForRaceStartAnimation ? -1 : initProgress.normProgress;

    if (initProgress.normProgress < 1) {
      void playLayoutAnimation(this.layout, 'Wheel_Driving', true);
      this.scaleAnimator.play(true);
      if (!setupForRaceStartAnimation) {
        this.bounceAnimator.play(true);
      }
    }

    this.updateCarY();
  }

  public async animateChange(progress: RaceTrackProgress): Promise<void> {
    const { player, initProgress } = this.config;

    const progressChanged = initProgress.normProgress !== progress.normProgress;

    if (!progressChanged) return;

    void playLayoutAnimation(this.layout, 'Smoke', false);
    await this.tweenTo(
      { currentRelY: progress.normProgress >= 1 ? 2 : progress.normProgress },
      500,
      'cubicOut',
      true,
    ).asyncOnComplete();
    if (progress.normProgress === 1) {
      this.setWheelAnimation(false);
      this.scaleAnimator.pauseOnLoop();
      this.bounceAnimator.pauseOnLoop();
      if (player.currentUser && this.parent) {
        await triggerParticleFX('Yellow_Glitter_From_Center', this.parent, this.x, this.y);
      }
    }
  }

  private setWheelAnimation(visible: boolean, immediate = false): void {
    const alphaTargetValue = visible ? 1 : 0;
    this.layout.tireWhooshesWrapper.tweenTo({ alpha: alphaTargetValue }, immediate ? 0 : 100, 'sinInOut', true);
  }

  private readonly updateCarY = (): void => {
    this.y = this.carRelYToY();
  };

  private carRelYToY(): number {
    const {
      currentRelY,
      config: {
        positions: { startPosition, preFinishedPosition, finishedPosition, preStartPosition },
      },
    } = this;

    if (currentRelY < 0) {
      return linear(preStartPosition.y, startPosition.y, currentRelY + 1);
    }

    if (currentRelY >= 1) {
      return linear(preFinishedPosition.y, finishedPosition.y, currentRelY - 1);
    }
    return linear(startPosition.y, preFinishedPosition.y, currentRelY);
  }
}
